import { tradeLogger } from '@flipster/utils/datadog';
import segment from '@flipster/utils/segment';
import { PerformanceEvent } from '@flipster/utils/segment/events/performance';

export type OrderEventContextInfo = Omit<
  PerformanceEvent['perf_order_submitted_latency'],
  | 'pre_api_duration'
  | 'api_duration'
  | 'post_api_duration'
  | 'duration'
  | 'websocket_order_response_duration'
  | 'error_category'
  | 'error_message'
>;

export interface OrderEventErrorInfo {
  type: 'api' | 'validation' | 'unknown';
  error: string | null;
}

type UnresolvedEventPair = [matcher: (context: OrderEventContext) => boolean, respondedTime: DOMHighResTimeStamp];
export class OrderEventTracker {
  #contexts: Record<string, OrderEventContext> = {};
  #pendingContexts: OrderEventContext[] = [];

  #unresolvedPositionMessages: Array<UnresolvedEventPair> = [];
  #unresolvedOrderMessages: Array<UnresolvedEventPair> = [];
  #unresolvedSpotOrderMessages: Array<UnresolvedEventPair> = [];
  ttl = 5000;

  static instance = new OrderEventTracker();
  static currentContext: OrderEventContext | null = null;

  public getContext(id: string): OrderEventContext | undefined {
    return this.#contexts[id];
  }

  public createContext(initialInfo: OrderEventContextInfo): OrderEventContext {
    const context = new OrderEventContext(initialInfo, this);
    this.#pendingContexts.push(context);
    return context;
  }

  public markContextId(context: OrderEventContext, id: string): OrderEventContext | undefined {
    const index = this.#pendingContexts.indexOf(context);
    if (index === -1) return;
    this.#pendingContexts.splice(index, 1);
    this.#contexts[id] = context;
    {
      const resolvedPositionMessage = this.#unresolvedPositionMessages.find(([matcher]) => matcher(context));
      if (resolvedPositionMessage) {
        const [, respondedTime] = resolvedPositionMessage;
        context.markSocketFinish(respondedTime);
      }
      this.#unresolvedPositionMessages = this.#unresolvedPositionMessages.filter(
        (unresolvedPair) => unresolvedPair !== resolvedPositionMessage,
      );
    }
    {
      const resolvedOrderMessage = this.#unresolvedOrderMessages.find(([matcher]) => matcher(context));
      if (resolvedOrderMessage) {
        const [, respondedTime] = resolvedOrderMessage;
        context.markSocketFinish(respondedTime);
      }
      this.#unresolvedOrderMessages = this.#unresolvedOrderMessages.filter(
        (unresolvedPair) => unresolvedPair !== resolvedOrderMessage,
      );
    }
    {
      const resolvedSpotOrderMessage = this.#unresolvedSpotOrderMessages.find(([matcher]) => matcher(context));
      if (resolvedSpotOrderMessage) {
        const [, respondedTime] = resolvedSpotOrderMessage;
        context.markSocketFinish(respondedTime);
      }
      this.#unresolvedSpotOrderMessages = this.#unresolvedSpotOrderMessages.filter(
        (unresolvedPair) => unresolvedPair !== resolvedSpotOrderMessage,
      );
    }
    return this.#contexts[id];
  }

  public withContext<T>(initialInfo: OrderEventContextInfo, fn: (context: OrderEventContext) => T): T {
    const context = this.createContext(initialInfo);
    OrderEventTracker.currentContext = context;
    return fn(context);
  }

  public remove(context: OrderEventContext) {
    if (OrderEventTracker.currentContext === context) {
      OrderEventTracker.currentContext = null;
    }
    if (context.requestId) {
      delete this.#contexts[context.requestId];
    } else {
      this.#pendingContexts = this.#pendingContexts.filter((ctx) => ctx !== context);
    }
  }

  public addPositionMessage(matcher: (context: OrderEventContext) => boolean) {
    const pair: UnresolvedEventPair = [matcher, performance.now()];
    this.#unresolvedPositionMessages.push(pair);
    setTimeout(() => {
      this.#unresolvedPositionMessages = this.#unresolvedPositionMessages.filter(
        (unresolvedPair) => unresolvedPair !== pair,
      );
    }, this.ttl);
  }

  public addOrderMessage(matcher: (context: OrderEventContext) => boolean) {
    const pair: UnresolvedEventPair = [matcher, performance.now()];
    this.#unresolvedOrderMessages.push(pair);
    setTimeout(() => {
      this.#unresolvedOrderMessages = this.#unresolvedOrderMessages.filter((unresolvedPair) => unresolvedPair !== pair);
    }, this.ttl);
  }

  public addSpotOrderMessage(matcher: (context: OrderEventContext) => boolean) {
    const pair: UnresolvedEventPair = [matcher, performance.now()];
    this.#unresolvedSpotOrderMessages.push(pair);
    setTimeout(() => {
      this.#unresolvedSpotOrderMessages = this.#unresolvedSpotOrderMessages.filter(
        (unresolvedPair) => unresolvedPair !== pair,
      );
    }, this.ttl);
  }
}

type MarkTarget = 'apiStartTime' | 'apiEndTime' | 'postApiEndTime';
export class OrderEventContext {
  #parent: OrderEventTracker;

  requestId: string | undefined;

  ttl = 5000;
  preApiStartTime = performance.now();
  apiStartTime = 0;
  apiEndTime = 0;
  postApiEndTime = 0;
  socketRespondedTime = 0;
  socketResponseSupported = true;
  type: 'insert' | 'update' | 'delete' = 'insert';

  #error: OrderEventErrorInfo | undefined;

  #apiFinished = false;
  #socketFinished = false;

  public constructor(
    public info: OrderEventContextInfo,
    parent: OrderEventTracker,
  ) {
    this.#parent = parent;
  }

  get socketFinished() {
    return this.#socketFinished;
  }

  set socketFinished(value: boolean) {
    this.#socketFinished = value;
    if (this.apiFinished) {
      this.#finish();
    }
  }

  get apiFinished() {
    return this.#apiFinished;
  }

  set apiFinished(value: boolean) {
    this.#apiFinished = value;
    if (this.socketFinished || !this.socketResponseSupported) {
      this.#finish();
    } else {
      setTimeout(() => this.#finish(), this.ttl);
    }
  }

  public mark(target: MarkTarget) {
    if (this[target] > 0) return;
    this[target] = performance.now();
  }

  public markError(error: OrderEventErrorInfo) {
    this.#error = error;
    this.socketResponseSupported = false;
  }

  public markRequestId(id: string) {
    this.requestId = id;
    this.#parent.markContextId(this, id);
  }

  get #durationSummary() {
    const preApiDurationMillis = this.apiStartTime - this.preApiStartTime;
    const apiDurationMillis = this.apiStartTime === 0 ? 0 : this.apiEndTime - this.apiStartTime;
    const postApiDurationMillis = this.postApiEndTime - this.apiEndTime;
    const durationMillis = this.postApiEndTime - this.preApiStartTime;
    const socketDurationMillis =
      !this.socketResponseSupported || this.apiStartTime === 0 ? 0 : this.socketRespondedTime - this.apiStartTime;
    return {
      pre_api_duration: preApiDurationMillis * 1_000_000,
      api_duration: apiDurationMillis * 1_000_000,
      post_api_duration: postApiDurationMillis * 1_000_000,
      duration: durationMillis * 1_000_000,
      websocket_order_response_duration: socketDurationMillis * 1_000_000,
    };
  }

  get #errorSummary() {
    if (!this.#error) {
      return {
        error_category: null,
        error_message: null,
      };
    }

    return {
      error_category: this.#error.type,
      error_message: this.#error.error,
    };
  }

  public markApiFinish() {
    this.apiFinished = true;
  }

  public markSocketFinish(time = performance.now()) {
    this.socketRespondedTime = time;
    this.socketFinished = true;
  }

  #report() {
    const params = {
      ...this.info,
      ...this.#durationSummary,
      ...this.#errorSummary,
    } satisfies PerformanceEvent['perf_order_submitted_latency'];
    tradeLogger.info('order_submitted_latency', params);
    segment.track('perf_order_submitted_latency', params);
  }

  #finish() {
    this.#report();
    this.#parent.remove(this);
  }
}
